import { lazy, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { isNil } from 'lodash';

import { bridgeAtom, isOpenChatAtom, sessionAtom, useSetValue, useValue, widgetPopupsStatesAtom } from '@/store';

import { AppEvents } from '@/utils';

import { addEvent } from '@/modules/api';
import { useEvent, useTheme } from '@/modules/common';
import { useBehaviour } from '@/modules/context';
import { EventType, PopupState, WidgetLayout, WidgetPopupType } from '@/modules/models';
import { useInitPopup, useInitPopupWithCloseBtn, useStuckPopup, useViewedProductsPopup } from '@/modules/widget/hooks';
import { useCrossSalePopup } from '@/modules/widget/hooks/useCrossSalePopup';

const DefaultWidget = lazy(() => import('./layouts').then((module) => ({ default: module.DefaultLayout })));
const SideWidget = lazy(() => import('./layouts').then((module) => ({ default: module.Side })));

export const Widget = () => {
  const { toggleChat } = useBehaviour();
  const {
    mediaMobile,
    widget: { widgetLayout },
  } = useTheme();

  const { widgetExclusions, tooltipExclusions, isEmbedded, widget, pageType, translations } = useValue(bridgeAtom);

  const [isCloseBtnVisible, setIsCloseBtnVisible] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tooltipText, setTooltipText] = useState(window.LLL(translations.widgetTooltip) as string);
  const [isHover, setIsHover] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState<WidgetPopupType | null>(null);
  const isOpenChat = useValue(isOpenChatAtom);
  const session = useValue(sessionAtom);

  const isTooltipOpenRef = useRef(false);
  const nextTooltipRef = useRef<WidgetPopupType | null>(null);
  const ignoreHoverRef = useRef(false);
  const hideTooltipAfterFadeOut = useRef(false);

  const setWidgetPopupsStates = useSetValue(widgetPopupsStatesAtom);

  const { crossSaleTooltip = false, stuckTooltip = false, productsTooltip = false, initTooltip = false } = widget || {};

  const isWidgetDisabled = Boolean(
    widgetExclusions?.some((exclusion) => new RegExp(exclusion).test(window.location.pathname))
  );
  const isTooltipDisabled = Boolean(tooltipExclusions?.includes(window.location.pathname));

  const isWidgetHidden = Boolean(isOpenChat || isWidgetDisabled || isEmbedded);
  const disabledPopup = isWidgetDisabled || isWidgetHidden || isTooltipDisabled;

  const widgetTitle = translations?.widgetTitle?.length
    ? translations.widgetTitle
    : (window.LLL(translations.widget?.title) as string);

  const toggleTooltip = useCallback((state?: boolean) => {
    setIsTooltipOpen((prevState) => (!isNil(state) ? state : !prevState));
  }, []);

  type InitKeys = keyof typeof translations.widget.initPopup;
  type HoverKeys = keyof typeof translations.widget.hoverPopup;

  const pageTypeInitMessage = (window.LLL(translations?.widget.initPopup?.[pageType as InitKeys]) as string) || '';
  const defaultInitMessage = window.LLL(translations?.widget.initPopup?.DEFAULT) as string;
  const pageTypeHoverMessage = (window.LLL(translations?.widget.hoverPopup?.[pageType as HoverKeys]) as string) || '';
  const defaultHoverMessage = (window.LLL(translations?.widget.hoverPopup?.DEFAULT) as string) || '';

  const getInitPopupMessage = useCallback(() => {
    return pageTypeInitMessage?.length ? pageTypeInitMessage : defaultInitMessage;
  }, [pageTypeInitMessage, defaultInitMessage]);

  const getHoverPopupMessage = useCallback(() => {
    if (translations.widgetTooltip?.length) {
      return translations.widgetTooltip;
    }

    if (pageTypeHoverMessage.length) {
      return pageTypeHoverMessage;
    }

    if (defaultHoverMessage?.length) {
      return defaultHoverMessage;
    }

    if (pageTypeInitMessage.length) {
      return pageTypeInitMessage;
    }

    return defaultInitMessage;
  }, [translations.widgetTooltip, pageTypeInitMessage, pageTypeHoverMessage, defaultInitMessage, defaultHoverMessage]);

  const getPopupText = useCallback(
    (popupType: WidgetPopupType) => {
      switch (popupType) {
        case WidgetPopupType.INIT:
        case WidgetPopupType.INIT_WITH_CLOSE_BTN:
          return getInitPopupMessage();

        case WidgetPopupType.STUCK_ON_PAGE:
          return window.LLL(translations.widget.stuckPopup);

        case WidgetPopupType.CROSS_SALE:
          return window.LLL(translations.widget.crossSalePopup);

        case WidgetPopupType.VIEWED_PRODUCTS:
          return window.LLL(translations.widget.viewedProductsPopup);

        case WidgetPopupType.HOVER:
          return getHoverPopupMessage();
      }
    },
    [translations.widget, getInitPopupMessage, getHoverPopupMessage]
  );

  const onToggleTooltipEvent = useCallback(
    (event: CustomEvent<boolean>) => {
      if (isTooltipDisabled) {
        return;
      }
      setActiveTooltip(WidgetPopupType.HOVER);
      const text = getPopupText(WidgetPopupType.HOVER) as string;
      setTooltipText(text);
      toggleTooltip(event.detail ?? null);
    },
    [getPopupText, isTooltipDisabled, toggleTooltip]
  );

  const hideTooltip = useCallback(() => {
    toggleTooltip(false);
    setActiveTooltip(null);
  }, [toggleTooltip]);

  const isMobile = window.matchMedia(`(${mediaMobile})`).matches;

  const toggleHover = useCallback(
    (state: boolean) => {
      const shouldIgnoreHover = ignoreHoverRef.current && state;
      if (isMobile || shouldIgnoreHover) {
        return;
      }

      setIsHover(state);

      const isDefaultTooltip = activeTooltip === WidgetPopupType.HOVER;

      if (!state && !isDefaultTooltip) {
        return;
      }

      if (activeTooltip === null && state) {
        const text = getPopupText(WidgetPopupType.HOVER) as string;
        setActiveTooltip(WidgetPopupType.HOVER);
        setTooltipText(text);
      } else if (!state && isDefaultTooltip) {
        setActiveTooltip(null);
      }

      toggleTooltip(state);
    },
    [toggleTooltip, activeTooltip, isMobile, getPopupText]
  );

  const setPopupState = useCallback(
    (popupType: WidgetPopupType, popupState: PopupState) => {
      if (popupType !== WidgetPopupType.INIT) {
        setWidgetPopupsStates((prevState) => ({
          ...prevState,
          [popupType]: popupState,
        }));
      } else {
        setWidgetPopupsStates((prevState) => ({
          ...prevState,
          [WidgetPopupType.INIT]: {
            ...prevState[WidgetPopupType.INIT],
            [pageType]: popupState,
          },
        }));
      }
    },
    [setWidgetPopupsStates, pageType]
  );

  const showInitTooltip = useCallback(
    (showCloseBtn?: boolean) => {
      const popup = showCloseBtn ? WidgetPopupType.INIT_WITH_CLOSE_BTN : WidgetPopupType.INIT;
      setPopupState(popup, PopupState.VISIBLE);
      if (!pageType) {
        return;
      }

      setIsCloseBtnVisible(Boolean(showCloseBtn));
      setActiveTooltip(popup);
      const text = getPopupText(popup);
      setTooltipText(text as string);
      toggleTooltip(true);
    },
    [pageType, toggleTooltip, setPopupState, getPopupText]
  );

  const closeInitTooltip = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      ignoreHoverRef.current = true;
      hideTooltipAfterFadeOut.current = true;
      toggleTooltip(false);
      setActiveTooltip(null);
      setWidgetPopupsStates((prevState) => ({
        ...prevState,
        [WidgetPopupType.INIT_WITH_CLOSE_BTN]: PopupState.CLOSED,
        [WidgetPopupType.INIT]: {
          ...prevState[WidgetPopupType.INIT],
          [pageType]: PopupState.SEEN,
        },
      }));
    },
    [pageType, toggleTooltip, setWidgetPopupsStates]
  );

  const showPopup = useCallback(
    (popupType: WidgetPopupType) => {
      if (popupType === WidgetPopupType.INIT_WITH_CLOSE_BTN || popupType === WidgetPopupType.INIT) {
        showInitTooltip(popupType === WidgetPopupType.INIT_WITH_CLOSE_BTN);
        return;
      }

      setPopupState(popupType, PopupState.VISIBLE);
      setActiveTooltip(popupType);
      const popupText = getPopupText(popupType) as string;
      setTooltipText(popupText);
      toggleTooltip(true);
    },
    [getPopupText, setPopupState, toggleTooltip, showInitTooltip]
  );

  const showNextTooltip = useCallback(() => {
    if (!nextTooltipRef.current) {
      return;
    }
    const nextTooltip = nextTooltipRef.current;
    nextTooltipRef.current = null;

    const timer = setTimeout(() => {
      showPopup(nextTooltip);
    }, 1000);
    return () => clearTimeout(timer);
  }, [showPopup]);

  const onTooltipFadeOut = useCallback(() => {
    ignoreHoverRef.current = false;
    setIsCloseBtnVisible(false);
    if (nextTooltipRef.current) {
      showNextTooltip();
      return;
    }

    const text = getPopupText(WidgetPopupType.HOVER) as string;
    setTooltipText(text);
  }, [showNextTooltip, getPopupText]);

  const startPopupWithDelay = useCallback(
    (popupType: WidgetPopupType, delay: number) => {
      const timer = setTimeout(() => {
        if (isTooltipOpenRef.current) {
          nextTooltipRef.current = popupType;
        } else {
          showPopup(popupType);
        }
      }, delay);

      return () => clearTimeout(timer);
    },
    [showPopup]
  );

  const onClick = useCallback(() => {
    toggleChat();

    addEvent({
      type: EventType.CLICKED_TOOLTIP,
      userSession: session,
      metadata: {
        pageType,
        tooltip: tooltipText,
        alternative: false,
      },
    });
  }, [tooltipText, pageType, session, toggleChat]);

  useEffect(() => {
    isTooltipOpenRef.current = isTooltipOpen;
  }, [isTooltipOpen]);

  useInitPopupWithCloseBtn({
    isDisabled: !initTooltip || disabledPopup,
    isWidgetHidden,
    startPopupWithDelay,
  });

  useInitPopup({
    isDisabled: !initTooltip || disabledPopup,
    isWidgetHidden,
    isHover,
    startPopupWithDelay,
    hideTooltip,
    setPopupState,
  });

  useViewedProductsPopup({
    isDisabled: !productsTooltip || disabledPopup,
    activePopup: activeTooltip,
    isHover,
    isWidgetHidden,
    setPopupState,
    startPopupWithDelay,
    hidePopup: hideTooltip,
  });

  useStuckPopup({
    isDisabled: !stuckTooltip || disabledPopup,
    isHover,
    hidePopup: hideTooltip,
    activePopup: activeTooltip,
    showPopup,
    setPopupState,
  });

  useCrossSalePopup({
    isDisabled: !crossSaleTooltip || disabledPopup,
    isHover,
    hidePopup: hideTooltip,
    startPopupWithDelay,
    setPopupState,
  });

  useEffect(() => {
    if (!isTooltipOpen || activeTooltip === WidgetPopupType.HOVER) {
      return;
    }

    addEvent({
      type: EventType.VIEWED_TOOLTIP,
      userSession: session,
      metadata: {
        pageType,
        tooltip: tooltipText,
        alternative: false,
      },
    });
  }, [isTooltipOpen, activeTooltip, tooltipText, session, pageType]);

  useEffect(() => {
    setIsTooltipOpen(false);
  }, [isOpenChat]);

  useEvent({ name: AppEvents.TOGGLE_TOOLTIP, handler: onToggleTooltipEvent });

  if (isOpenChat || isWidgetDisabled || isEmbedded) {
    return null;
  }

  switch (widgetLayout) {
    case WidgetLayout.DEFAULT:
      return (
        <DefaultWidget
          widgetTitle={widgetTitle}
          closeBtn={isCloseBtnVisible}
          isTooltipOpen={isTooltipOpen}
          tooltipText={tooltipText}
          onTooltipHided={onTooltipFadeOut}
          toggleHover={toggleHover}
          closeInitTooltip={closeInitTooltip}
          onClick={onClick}
        />
      );

    case WidgetLayout.SIDE:
      return (
        <SideWidget
          widgetTitle={widgetTitle}
          tooltipText={tooltipText}
          isTooltipOpen={isTooltipOpen}
          closeInitTooltip={closeInitTooltip}
          closeBtn={isCloseBtnVisible}
          toggleHover={toggleHover}
          onTooltipHidden={onTooltipFadeOut}
          onClick={onClick}
        />
      );

    default:
      return (
        <DefaultWidget
          widgetTitle={widgetTitle}
          closeBtn={isCloseBtnVisible}
          isTooltipOpen={isTooltipOpen}
          tooltipText={tooltipText}
          onTooltipHided={onTooltipFadeOut}
          toggleHover={toggleHover}
          closeInitTooltip={closeInitTooltip}
          onClick={onClick}
        />
      );
  }
};
